import { isMatchUpcoming, isMatchFinished } from '@/lib/matchTime';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { matchesAPI, sportsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Calendar, Users, MapPin, Search, Plus, Clock
} from 'lucide-react';
import { 
  formatDate, formatTime, getRelativeTime, getStatusColorClass, getStatusLabel 
} from '@/lib/utils';

export const MatchesPage = () => {
  const [loading, setLoading] = useState(true);
  const [myMatches, setMyMatches] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [sports, setSports] = useState([]);
  const [selectedSport, setSelectedSport] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [myRes, allRes, sportsRes] = await Promise.all([
          matchesAPI.getMyMatches(),
          matchesAPI.getAll({ is_public: true }),
          sportsAPI.getAll()
        ]);
        setMyMatches(myRes.data);
        setAllMatches(allRes.data);
        setSports(sportsRes.data);
      } catch (error) {
        console.error('Error fetching matches:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filterMatches = (matches) => {
    return matches.filter(match => {
      const matchesSearch = match.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            match.location_name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSport = selectedSport === 'all' || match.sport_id === selectedSport;
      const matchesStatus = statusFilter === 'all' || match.status === statusFilter;
      return matchesSearch && matchesSport && matchesStatus;
    });
  };

  const upcomingMatches = myMatches.filter(m => 
    isMatchUpcoming(m)
  );
  upcomingMatches.sort((a, b) => new Date(a.date_time) - new Date(b.date_time));
  const pastMatches = myMatches.filter(m => 
    !isMatchUpcoming(m)
  );

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-12 w-full" />
        <div className="space-y-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="matches-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Partite</h1>
          <p className="text-muted-foreground">Gestisci e partecipa alle partite</p>
        </div>
        <Link to="/matches/new">
          <Button data-testid="create-match-btn">
            <Plus className="h-4 w-4 mr-2" />
            Crea Partita
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            aria-label="Cerca partite"
            placeholder="Cerca per titolo o luogo..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-matches"
          />
        </div>
        <details className="rounded-lg border p-2 sm:min-w-[160px]">
          <summary className="cursor-pointer text-sm font-medium px-2 py-1">Filtri{selectedSport !== 'all' || statusFilter !== 'all' ? ' · attivi' : ''}</summary>
          <div className="flex flex-col gap-2 mt-2">
        <Select value={selectedSport} onValueChange={setSelectedSport}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Sport" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli sport</SelectItem>
            {sports.map(sport => (
              <SelectItem key={sport.sport_id} value={sport.sport_id}>
                {sport.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[150px]">
            <SelectValue placeholder="Stato" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="confirmed">Confermate</SelectItem>
            <SelectItem value="draft">Bozza</SelectItem>
            <SelectItem value="completed">Completate</SelectItem>
          </SelectContent>
        </Select>
          </div>
        </details>
      </div>

      <Tabs defaultValue="my" className="space-y-6">
        <TabsList>
          <TabsTrigger value="my">Le Mie ({myMatches.length})</TabsTrigger>
          <TabsTrigger value="public">Pubbliche ({allMatches.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="my" className="space-y-6">
          {/* Upcoming */}
          <div className="space-y-3">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Prossime ({filterMatches(upcomingMatches).length})
            </h2>
            {filterMatches(upcomingMatches).length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Nessuna partita in programma</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filterMatches(upcomingMatches).map(match => (
                  <MatchCard key={match.match_id} match={match} />
                ))}
              </div>
            )}
          </div>

          {/* Past */}
          {filterMatches(pastMatches).length > 0 && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Clock className="h-5 w-5 text-muted-foreground" />
                Passate ({filterMatches(pastMatches).length})
              </h2>
              <div className="space-y-3">
                {filterMatches(pastMatches).map(match => (
                  <MatchCard key={match.match_id} match={match} />
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="public">
          {filterMatches(allMatches).length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nessuna partita pubblica trovata</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filterMatches(allMatches).map(match => (
                <MatchCard key={match.match_id} match={match} showLeague />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Live countdown "Tra 2g 4h" — updates every minute.
const CardCountdown = ({ dateTime }) => {
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);
  if (!dateTime) return null;
  const diff = new Date(dateTime).getTime() - now;
  if (diff <= 0) return null;
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days >= 1) return `Tra ${days}g ${hours % 24}h`;
  if (hours >= 1) return `Tra ${hours}h ${minutes % 60}m`;
  return `Tra ${minutes}m`;
};

// Pick the top stripe gradient + capacity badge color from match status + capacity.
const useMatchVisualState = (match) => {
  const isPast = isMatchFinished(match);
  const taken = match.participants_count || 0;
  const max = match.max_participants || 10;
  const left = max - taken;
  const ratio = max > 0 ? taken / max : 0;

  if (match.status === 'completed') {
    return {
      stripe: 'from-slate-500 to-slate-600',
      capacityClass: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
      capacityLabel: 'Completata',
      isPast: true,
    };
  }
  if (match.status === 'cancelled') {
    return {
      stripe: 'from-rose-500 to-pink-500',
      capacityClass: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200',
      capacityLabel: 'Annullata',
      isPast,
    };
  }
  if (isPast) {
    return {
      stripe: 'from-slate-400 to-slate-500',
      capacityClass: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
      capacityLabel: 'Passata',
      isPast: true,
    };
  }
  // Live (in corso)
  if (match.status === 'ongoing') {
    return {
      stripe: 'from-blue-500 to-indigo-600',
      capacityClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
      capacityLabel: '🔴 IN CORSO',
      isPast: false,
    };
  }
  // Future, capacity-based
  if (left <= 0) {
    return {
      stripe: 'from-rose-500 to-pink-500',
      capacityClass: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200',
      capacityLabel: 'Titolari pieni · iscriviti riserva',
      isPast: false,
    };
  }
  if (left === 1 || ratio >= 0.85) {
    return {
      stripe: 'from-amber-500 to-orange-500',
      capacityClass: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
      capacityLabel: left === 1 ? 'Solo 1 posto!' : `Solo ${left} posti rimasti`,
      isPast: false,
    };
  }
  return {
    stripe: 'from-emerald-500 to-blue-500',
    capacityClass: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
    capacityLabel: `${left} posti liberi`,
    isPast: false,
  };
};

const MatchCard = ({ match, showLeague = false }) => {
  const visual = useMatchVisualState(match);
  const countdown = CardCountdown({ dateTime: match.date_time });

  return (
    <Link to={`/matches/${match.match_id}`} data-testid={`match-card-${match.match_id}`}>
      <Card className={`card-interactive overflow-hidden border-2 transition hover:shadow-md ${visual.isPast ? 'opacity-80' : ''}`}>
        {/* Top stripe with countdown + date */}
        <div className={`bg-gradient-to-r ${visual.stripe} text-white px-4 py-2 flex items-center justify-between text-sm font-medium`}>
          <span className="flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            {countdown || (visual.isPast ? formatDate(match.date_time, { weekday: 'short' }) : formatTime(match.date_time))}
          </span>
          <span className="text-xs bg-white/20 px-2 py-0.5 rounded font-medium">
            {formatDate(match.date_time, { weekday: 'short', day: 'numeric', month: 'short' })} · {formatTime(match.date_time)}
          </span>
        </div>

        <CardContent className="p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <Badge variant="secondary" className="text-xs">{match.sport_name}</Badge>
                {match.user_team && (
                  <Badge variant="outline" className="text-xs">Team {match.user_team}</Badge>
                )}
              </div>
              <h3 className="font-semibold text-lg leading-tight">{match.title}</h3>
              {showLeague && match.league_name && (
                <p className="text-sm text-muted-foreground mt-0.5">{match.league_name}</p>
              )}
              <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {match.location_name}
              </p>
            </div>
          </div>

          {/* Capacity badge — only for non-completed matches */}
          {match.status !== 'completed' && (
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${visual.capacityClass}`}>
              <Users className="h-3.5 w-3.5" />
              {visual.capacityLabel}
            </div>
          )}

          {/* Show result if completed */}
          {match.status === 'completed' && match.result && (
            <div className="pt-3 border-t flex items-center justify-around bg-slate-900 text-white rounded-lg p-3 -mx-1">
              <div className="text-center">
                <p className="text-xs opacity-70">Team A</p>
                <p className="text-2xl font-bold">{match.result.team_a_score}</p>
              </div>
              <span className="text-muted-foreground opacity-70">-</span>
              <div className="text-center">
                <p className="text-xs opacity-70">Team B</p>
                <p className="text-2xl font-bold">{match.result.team_b_score}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

export default MatchesPage;
